// /var/www/python/womsys/frontend/src/components/SapInstellingenTab.js
//
// Component voor de SAP-instellingen, gebruikt binnen de Instellingen pagina als tab

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './SapInstellingenTab.css';

const SapInstellingenTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subTab, setSubTab] = useState('tijden'); // 'tijden' | 'codes' | 'monteurs'

  const [tijden, setTijden] = useState({
    sap_dag_start: '06:00',
    sap_dag_eind: '18:00',
    sap_weekend_start_dag: 6,
    sap_weekend_start_tijd: '18:00',
    sap_weekend_eind_dag: 1,
    sap_weekend_eind_tijd: '06:00'
  });

  const [codes, setCodes] = useState([]);
  const [monteurs, setMonteurs] = useState([]);
  const [categorieen, setCategorieen] = useState([]);

  useEffect(() => {
    laadAlles();
  }, []);

  const laadAlles = async () => {
    setLoading(true);
    try {
      const [tijdenRes, codesRes, monteursRes, catRes] = await Promise.all([
        api.get('/uren/sap-tijden'),
        api.get('/uren/sap-codes'),
        api.get('/gebruikers'),
        api.get('/plan-categorieen').catch(() => ({ data: { data: [] } }))
      ]);
      setCategorieen(catRes.data.data || []);
      if (tijdenRes.data.tijden) {
        const t = tijdenRes.data.tijden;
        setTijden({
          sap_dag_start: (t.sap_dag_start || '06:00').substring(0, 5),
          sap_dag_eind: (t.sap_dag_eind || '18:00').substring(0, 5),
          sap_weekend_start_dag: t.sap_weekend_start_dag || 6,
          sap_weekend_start_tijd: (t.sap_weekend_start_tijd || '18:00').substring(0, 5),
          sap_weekend_eind_dag: t.sap_weekend_eind_dag || 1,
          sap_weekend_eind_tijd: (t.sap_weekend_eind_tijd || '06:00').substring(0, 5)
        });
      }
      setCodes(codesRes.data.codes || []);
      const gebs = monteursRes.data.gebruikers || monteursRes.data || [];
      setMonteurs(gebs.filter(g => g.actief !== false));
    } catch (e) {
      console.error('Laad fout:', e);
    } finally {
      setLoading(false);
    }
  };

  const saveTijden = async () => {
    setSaving(true);
    try {
      await api.put('/uren/sap-tijden', tijden);
      alert('✓ Tijden opgeslagen');
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const addCode = () => {
    setCodes([...codes, {
      code: '', omschrijving: '',
      is_default: false, is_overwerk: false,
      actief: true, volgorde: codes.length
    }]);
  };

  const updateCode = (idx, field, value) => {
    const nieuw = [...codes];
    nieuw[idx] = { ...nieuw[idx], [field]: value };
    if (field === 'is_default' && value) nieuw.forEach((c, i) => { if (i !== idx) c.is_default = false; });
    if (field === 'is_overwerk' && value) nieuw.forEach((c, i) => { if (i !== idx) c.is_overwerk = false; });
    setCodes(nieuw);
  };

  const removeCode = (idx) => setCodes(codes.filter((_, i) => i !== idx));

  const saveCodes = async () => {
    if (codes.find(c => !c.code || !c.omschrijving)) {
      alert('Alle codes moeten een code en omschrijving hebben');
      return;
    }
    setSaving(true);
    try {
      await api.post('/uren/sap-codes', { codes });
      alert('✓ Codes opgeslagen');
      laadAlles();
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const updatePrefix = (id, prefix) => {
    setMonteurs(monteurs.map(m => m.id === id ? { ...m, sap_prestatie_prefix: prefix } : m));
  };

  const savePrefix = async (monteur) => {
    try {
      await api.put(`/gebruikers/${monteur.id}`, {
        sap_prestatie_prefix: monteur.sap_prestatie_prefix || 'UI070'
      });
    } catch (e) {
      alert('Opslaan mislukt voor ' + monteur.voornaam + ': ' + (e.response?.data?.message || e.message));
    }
  };

  // Categorieen helpers
  const addCategorie = () => {
    setCategorieen([...categorieen, {
      id: null, titel: '', sap_code: '',
      default_uren: null, default_ordernummer: '',
      actief: true, volgorde: categorieen.length
    }]);
  };

  const updateCategorie = (idx, field, value) => {
    const nieuw = [...categorieen];
    nieuw[idx] = { ...nieuw[idx], [field]: value };
    setCategorieen(nieuw);
  };

  const removeCategorie = async (idx) => {
    const c = categorieen[idx];
    if (c.id) {
      if (!window.confirm('Verwijder "' + c.titel + '"? Lopende planning blijft staan.')) return;
      try {
        await api.delete('/plan-categorieen/' + c.id);
        laadAlles();
      } catch (e) {
        alert('Verwijderen mislukt: ' + (e.response?.data?.message || e.message));
      }
    } else {
      setCategorieen(categorieen.filter((_, i) => i !== idx));
    }
  };

  const saveCategorieen = async () => {
    if (categorieen.find(c => !c.titel || !c.sap_code)) {
      alert('Alle categorieen moeten een titel en SAP-code hebben');
      return;
    }
    setSaving(true);
    try {
      for (const c of categorieen) {
        const body = {
          titel: c.titel,
          sap_code: c.sap_code,
          volgorde: c.volgorde || 0,
          actief: c.actief !== false,
          default_uren: c.default_uren === '' || c.default_uren == null ? null : parseFloat(c.default_uren),
          default_ordernummer: c.default_ordernummer || null
        };
        if (c.id) {
          await api.put('/plan-categorieen/' + c.id, body);
        } else {
          await api.post('/plan-categorieen', body);
        }
      }
      alert('✓ Categorieen opgeslagen');
      laadAlles();
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="sap-loading">Laden...</div>;

  return (
    <div className="sap-tab-content">
      <h2>🔌 SAP Instellingen</h2>

      <div className="sap-subtabs">
        <button
          className={subTab === 'tijden' ? 'active' : ''}
          onClick={() => setSubTab('tijden')}
        >🕐 Tijden</button>
        <button
          className={subTab === 'codes' ? 'active' : ''}
          onClick={() => setSubTab('codes')}
        >📋 Alg nrs</button>
        <button
          className={subTab === 'monteurs' ? 'active' : ''}
          onClick={() => setSubTab('monteurs')}
        >👤 Monteurs</button>
        <button
          className={subTab === 'categorieen' ? 'active' : ''}
          onClick={() => setSubTab('categorieen')}
        >📅 Categorieën</button>
      </div>

      {subTab === 'tijden' && (
        <div className="sap-section">
          <p className="sap-section-info">
            Bepaalt wanneer een uur dag (UI___D), nacht (UI___N) of weekend (UI___W) is.
          </p>

          <div className="sap-veld-groep">
            <h3>📅 Dag (ma t/m vr)</h3>
            <div className="sap-row">
              <label>
                Dag start
                <input type="time"
                  value={tijden.sap_dag_start}
                  onChange={e => setTijden({ ...tijden, sap_dag_start: e.target.value })} />
              </label>
              <label>
                Dag eind
                <input type="time"
                  value={tijden.sap_dag_eind}
                  onChange={e => setTijden({ ...tijden, sap_dag_eind: e.target.value })} />
              </label>
            </div>
            <p className="sap-hint">Tussen deze tijden = dag. Daarbuiten ma-vr = nacht.</p>
          </div>

          <div className="sap-veld-groep">
            <h3>🏠 Weekend</h3>
            <div className="sap-row">
              <label>
                Start (zaterdag vanaf)
                <input type="time"
                  value={tijden.sap_weekend_start_tijd}
                  onChange={e => setTijden({ ...tijden, sap_weekend_start_tijd: e.target.value })} />
              </label>
              <label>
                Eind (maandag tot)
                <input type="time"
                  value={tijden.sap_weekend_eind_tijd}
                  onChange={e => setTijden({ ...tijden, sap_weekend_eind_tijd: e.target.value })} />
              </label>
            </div>
            <p className="sap-hint">
              Zaterdag {tijden.sap_weekend_start_tijd} — maandag {tijden.sap_weekend_eind_tijd} = weekend.
            </p>
          </div>

          <button className="sap-save-btn" onClick={saveTijden} disabled={saving}>
            {saving ? 'Opslaan...' : '💾 Tijden opslaan'}
          </button>
        </div>
      )}

      {subTab === 'codes' && (
        <div className="sap-section">
          <p className="sap-section-info">
            Algemene nummers voor de Alg nr. kolom. Markeer 1 als <strong>default</strong> (standaard) en 1 als <strong>overwerk</strong>.
          </p>

          <div className="sap-codes-tabel">
            <div className="sap-codes-head">
              <span>Code</span>
              <span>Omschrijving</span>
              <span>Default</span>
              <span>Overwerk</span>
              <span>Actief</span>
              <span></span>
            </div>
            {codes.map((c, idx) => (
              <div key={idx} className="sap-codes-row">
                <input type="text" placeholder="0011" value={c.code} maxLength={10}
                  onChange={e => updateCode(idx, 'code', e.target.value)} />
                <input type="text" placeholder="Omschrijving" value={c.omschrijving}
                  onChange={e => updateCode(idx, 'omschrijving', e.target.value)} />
                <input type="checkbox" checked={c.is_default}
                  onChange={e => updateCode(idx, 'is_default', e.target.checked)} />
                <input type="checkbox" checked={c.is_overwerk}
                  onChange={e => updateCode(idx, 'is_overwerk', e.target.checked)} />
                <input type="checkbox" checked={c.actief !== false}
                  onChange={e => updateCode(idx, 'actief', e.target.checked)} />
                <button className="sap-delete-btn" onClick={() => removeCode(idx)}>🗑️</button>
              </div>
            ))}
          </div>

          <button className="sap-add-btn" onClick={addCode}>+ Code toevoegen</button>
          <button className="sap-save-btn" onClick={saveCodes} disabled={saving}>
            {saving ? 'Opslaan...' : '💾 Alle codes opslaan'}
          </button>
        </div>
      )}

      {subTab === 'categorieen' && (
        <div className="sap-section">
          <p className="sap-section-info">
            Categorieën die je kunt inplannen via Planning → "+ Werkoverleg / extra plannen". Elke categorie krijgt z'n eigen Alg nr in de SAP-export.
          </p>

          <datalist id="sap-codes-suggesties">
            {codes.map(co => <option key={co.code} value={co.code}>{co.omschrijving}</option>)}
          </datalist>
          <div className="sap-codes-tabel">
            <div className="sap-codes-head" style={{ gridTemplateColumns: '1.4fr 0.7fr 0.6fr 0.9fr 0.5fr 40px' }}>
              <span>Titel</span>
              <span>SAP code</span>
              <span>Default uren</span>
              <span>Vast ordernr</span>
              <span>Actief</span>
              <span></span>
            </div>
            {categorieen.map((c, idx) => (
              <div key={idx} className="sap-codes-row" style={{ gridTemplateColumns: '1.4fr 0.7fr 0.6fr 0.9fr 0.5fr 40px' }}>
                <input type="text" placeholder="Werkoverleg" value={c.titel || ''}
                  onChange={e => updateCategorie(idx, 'titel', e.target.value)} />
                <input type="text" placeholder="0073" maxLength={10}
                  list="sap-codes-suggesties"
                  value={c.sap_code || ''}
                  onChange={e => updateCategorie(idx, 'sap_code', e.target.value)} />
                <input type="number" step="0.5" min="0" max="24" placeholder="leeg"
                  value={c.default_uren ?? ''}
                  onChange={e => updateCategorie(idx, 'default_uren', e.target.value)} />
                <input type="text" placeholder="optioneel" value={c.default_ordernummer || ''}
                  onChange={e => updateCategorie(idx, 'default_ordernummer', e.target.value)} />
                <input type="checkbox" checked={c.actief !== false}
                  onChange={e => updateCategorie(idx, 'actief', e.target.checked)} />
                <button className="sap-delete-btn" onClick={() => removeCategorie(idx)}>🗑️</button>
              </div>
            ))}
          </div>

          <button className="sap-add-btn" onClick={addCategorie}>+ Categorie toevoegen</button>
          <button className="sap-save-btn" onClick={saveCategorieen} disabled={saving}>
            {saving ? 'Opslaan...' : '💾 Categorieën opslaan'}
          </button>
        </div>
      )}

      {subTab === 'monteurs' && (
        <div className="sap-section">
          <p className="sap-section-info">
            Per monteur eigen prestatie-prefix. Default is <code>UI070</code>.
            Voorbeeld: Dennis krijgt <code>UI071</code> → systeem maakt automatisch <code>UI071D/N/W</code>.
          </p>

          <div className="sap-monteurs-lijst">
            {monteurs.map(m => (
              <div key={m.id} className="sap-monteur-row">
                <span className="sap-monteur-naam">
                  {m.voornaam} {m.achternaam}
                </span>
                <input type="text" maxLength={10} placeholder="UI070"
                  value={m.sap_prestatie_prefix || 'UI070'}
                  onChange={e => updatePrefix(m.id, e.target.value)}
                  onBlur={() => savePrefix(m)} />
                <span className="sap-monteur-preview">
                  → {m.sap_prestatie_prefix || 'UI070'}D / N / W
                </span>
              </div>
            ))}
          </div>

          <p className="sap-hint">Wordt opgeslagen wanneer je het veld verlaat.</p>
        </div>
      )}
    </div>
  );
};

export default SapInstellingenTab;
